import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Create-group form. The Kind/Select dropdowns show plain-language labels ("View options"
 * / "Prompt options", "Pick one" / "Pick several") while keeping the underlying option
 * VALUES (`axis`/`modifier`, `single`/`multi`) unchanged on the wire. Default stays
 * axis/single so the request payload matches the prior behavior.
 *
 * P2 (round-trip audit): `order` is sent explicitly. The server lists groups
 * `ORDER BY order`, but creates left it at the default 0, so every group tied on zero and
 * the panel's order was whatever the DB returned. `nextOrder` (from OptionsPanel, which
 * owns the list) appends the new group after the existing ones.
 */
import { useRef, useState } from "react";
import { ActionBar } from "../ui/ActionBar.js";
import { useServices } from "../app/context.js";
import type { OptionGroup, OptionGroupKind, OptionSelect } from "@genz/platform";
import { KIND_LABELS, SELECT_LABELS } from "./labels.js";

export function CreateGroupForm({ nextOrder, onCreated }: {
  nextOrder: number;
  onCreated: (g: OptionGroup) => void;
}) {
  const { client } = useServices();
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<OptionGroupKind>("axis");
  const [select, setSelect] = useState<OptionSelect>("single");
  const [busy, setBusy] = useState(false);
  const fieldRef = useRef<HTMLInputElement>(null);
  const [fieldError, setFieldError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!key.trim()) return;
    setBusy(true);
    setError(null);
    setFieldError(false);
    try {
      const created = await client.adminCreateOptionGroup({
        key: key.trim(),
        label: label.trim() || key.trim(),
        kind,
        select,
        order: nextOrder,
      });
      onCreated(created);
      setKey("");
      setLabel("");
      setKind("axis");
      setSelect("single");
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      setError(detail);
      if (/duplicate|already|key exists|value exists|409/i.test(detail)) {
        setFieldError(true);
        requestAnimationFrame(() => fieldRef.current?.focus());
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card options-form-row" data-testid="option-create-group">
      <div className="field">
        <label htmlFor="option-create-group-key">Key</label>
        <input
          id="option-create-group-key"
          className="input"
          type="text"
          value={key}
          data-testid="option-create-group-key"
          onChange={(e) => { setKey(e.target.value); if (fieldError) { setFieldError(false); setError(null); } }}
          ref={fieldRef}
          aria-invalid={fieldError || undefined}
          aria-describedby={fieldError ? "option-create-group-field-error" : undefined}
        />
        {fieldError && <span id="option-create-group-field-error" className="field-error" role="alert"><ErrorNotice detail="duplicate option value" /><span>Choose a different key, then try again.</span></span>}
      </div>
      <label className="field">
        <span>Label</span>
        <input
          className="input"
          type="text"
          value={label}
          data-testid="option-create-group-label"
          onChange={(e) => setLabel(e.target.value)}
        />
      </label>
      <label className="field">
        <span>Type</span>
        <select
          className="input"
          value={kind}
          data-testid="option-create-group-kind"
          onChange={(e) => setKind(e.target.value as OptionGroupKind)}
        >
          <option value="axis">{KIND_LABELS.axis}</option>
          <option value="modifier">{KIND_LABELS.modifier}</option>
        </select>
      </label>
      <label className="field">
        <span>Selection</span>
        <select
          className="input"
          value={select}
          data-testid="option-create-group-select"
          onChange={(e) => setSelect(e.target.value as OptionSelect)}
        >
          <option value="single">{SELECT_LABELS.single}</option>
          <option value="multi">{SELECT_LABELS.multi}</option>
        </select>
      </label>
      {/* Rule 7. While this form is open it REPLACES "+ New option group", so it carries the
          Options panel's one primary — in the bar, at the bottom of the form. */}
      <ActionBar
        label="Create option group"
        primary={
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || !key.trim()}
            data-testid="option-create-group-submit"
            onClick={() => void submit()}
          >
            {busy ? "Creating…" : "Create group"}
          </button>
        }
      />
      {error && (
        <span role="alert" data-testid="option-create-group-error" className="faint">
          <ErrorNotice detail={error} />
        </span>
      )}
    </div>
  );
}
